import React, { useEffect, useState } from 'react';
import { Typography, TextField, Button, makeStyles, Paper, Theme, Grid } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import logo from 'images/simplify_logo2C.png'; // adjust path if needed
import illustration from 'images/illustration_calendar.png'; // import your custom image

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2)
  },
  paper: {
    maxWidth: 460,
    width: '100%',
    textAlign: 'center',
    padding: theme.spacing(4, 3),
    boxShadow: 'none',
    backgroundColor: 'transparent'
  },
  logo: {
    height: 75
  },
  image: {
    maxWidth: '100%',
    height: 200,
    marginBottom: theme.spacing(4)
  },
  inputContainer: {
    marginTop: theme.spacing(2)
  },
  textField: {
    marginBottom: theme.spacing(2)
  },
  alert: {
    marginBottom: theme.spacing(2),
    textAlign: 'left'
  },
  bookButton: {
    textTransform: 'none',
    padding: theme.spacing(1.5),
    fontWeight: 600,
    borderRadius: 8
  }
}));

interface Props {
  bookingSetting: any;
  initialQuotation?: string;
  // Shown above the form, e.g. when the link the customer opened is no longer valid
  notice?: string | null;
  isSubmitting?: boolean;
  // Resolves to an error message for the customer, or null when access was granted
  onSubmit: (quotation: string, pin: string) => Promise<string | null>;
}

const BookingLandingPage: React.FC<Props> = ({ bookingSetting, initialQuotation = '', notice, isSubmitting = false, onSubmit }) => {
  const classes = useStyles();
  const [quotation, setQuotation] = useState(initialQuotation);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);

  // The quotation number from an old link arrives after the tenant is resolved
  useEffect(() => {
    if (initialQuotation) {
      setQuotation(initialQuotation);
    }
  }, [initialQuotation]);

  const canSubmit = quotation.trim() !== '' && pin.length === 6 && !isSubmitting;

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!canSubmit) {
      return;
    }

    setError(null);
    const message = await onSubmit(quotation.trim(), pin);
    if (message) {
      setError(message);
      setPin('');
    }
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <Grid container spacing={2}>
          <Grid item xs={12} container justify='center'>
            <img src={bookingSetting.LogoUrl ? bookingSetting.LogoUrl : logo} alt='Simplify Logo' className={classes.logo} />
          </Grid>
          <Grid item xs={12} container justify='center'>
            <img src={illustration} alt='Calendar Illustration' className={classes.image} />
          </Grid>
        </Grid>

        <Typography variant='h6' gutterBottom>
          Skip the line. Book online.
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          To continue, please provide your quotation number and PIN.
        </Typography>
        <form className={classes.inputContainer} onSubmit={handleSubmit} noValidate>
          {notice && !error && (
            <Alert severity='info' className={classes.alert}>
              {notice}
            </Alert>
          )}
          {error && (
            <Alert severity='error' className={classes.alert}>
              {error}
            </Alert>
          )}
          <TextField
            id='booking-quotation-number'
            label='Quotation Number'
            variant='outlined'
            fullWidth
            value={quotation}
            onChange={e => setQuotation(e.target.value)}
            className={classes.textField}
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            id='booking-pin'
            label='PIN'
            variant='outlined'
            fullWidth
            value={pin}
            onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className={classes.textField}
            helperText="Don't have a PIN? Please contact your service provider."
            inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code' }}
          />
          <Button type='submit' fullWidth variant='contained' disableElevation color='primary' disabled={!canSubmit} className={classes.bookButton}>
            {'Continue →'}
          </Button>
        </form>
      </Paper>
    </div>
  );
};

export default BookingLandingPage;
